import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/feature/DramaCard";
import FilterPanel from "@/components/feature/FilterPanel";
import { dramas } from "@/mocks/dramas";
import { useDramaFilter, sortLabels, type SortKey } from "@/hooks/useDramaFilter";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 9;

export default function Category() {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type") || "";
  const [filterOpen, setFilterOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filter = useDramaFilter(dramas, typeParam ? [typeParam] : []);
  const {
    selectedTypes,
    setSelectedTypes,
    selectedYears,
    selectedRegions,
    sortKey,
    setSortKey,
    toggleType,
    toggleYear,
    toggleRegion,
    reset,
    results,
    activeCount,
  } = filter;

  usePageMeta({
    title: "电视剧分类 - 跨年份类型筛选 - 波多野结衣家庭教师",
    description:
      "按类型、年份与地区筛选站内电视剧，支持多选条件、热度与评分排序，快速找到想看的都市、古装、悬疑、青春等剧集，并查看完整剧情介绍。",
    keywords: "电视剧分类,剧集筛选,都市剧,古装剧,悬疑剧,剧情介绍,波多野结衣家庭教师",
  });

  useEffect(() => {
    if (typeParam) {
      setSelectedTypes([typeParam]);
      setVisibleCount(PAGE_SIZE);
    }
  }, [typeParam, setSelectedTypes]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedTypes, selectedYears, selectedRegions, sortKey]);

  useEffect(() => {
    document.body.style.overflow = filterOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [filterOpen]);

  const visibleResults = useMemo(
    () => results.slice(0, visibleCount),
    [results, visibleCount],
  );

  const filterPanel = (
    <FilterPanel
      selectedTypes={selectedTypes}
      selectedYears={selectedYears}
      selectedRegions={selectedRegions}
      onToggleType={toggleType}
      onToggleYear={toggleYear}
      onToggleRegion={toggleRegion}
      onReset={reset}
    />
  );

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-[72px]">
        <section className="border-b border-background-200/70 bg-background-100/50">
          <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 font-label text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-accent-500">首页</Link>
              <i className="ri-arrow-right-s-line text-sm opacity-60"></i>
              <span className="text-foreground-800">电视剧分类</span>
            </nav>
            <h1 className="mt-4 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl">
              电视剧分类与筛选
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              按类型、年份、地区组合筛选，支持多选与热度排序。每一部剧都附有剧情梗概、角色分析与章节目录，方便你先读懂故事再决定追不追。
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="font-label text-[11px] tracking-[0.18em] text-foreground-500">热门筛选</span>
              {["悬疑", "古装", "都市", "青春"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleType(tag)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 font-label text-xs transition-colors ${
                    selectedTypes.includes(tag)
                      ? "border-accent-500 bg-accent-500 text-background-50"
                      : "border-background-300/60 text-foreground-700 hover:border-accent-500/60 hover:text-accent-600"
                  }`}
                >
                  {tag}剧
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <div className="grid gap-6 lg:grid-cols-[276px_1fr] lg:gap-8">
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                {filterPanel}
                <div className="mt-4 rounded-lg border border-background-200/70 bg-background-100/60 p-4">
                  <p className="font-label text-[11px] uppercase tracking-[0.24em] text-accent-600">说明</p>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                    同一维度内为「或」关系，不同类型之间为「且」关系。当前演示数据集共 {dramas.length} 部剧集。
                  </p>
                </div>
              </div>
            </aside>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200/70 bg-background-100/70 px-4 py-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFilterOpen(true)}
                    className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300/60 px-3 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500 lg:hidden"
                  >
                    <i className="ri-filter-3-line text-sm"></i>
                    筛选
                    {activeCount > 0 && (
                      <span className="rounded-full bg-primary-500 px-1.5 text-[10px] text-background-50">
                        {activeCount}
                      </span>
                    )}
                  </button>
                  <p className="font-label text-xs text-foreground-600">
                    共 <span className="text-foreground-950">{results.length}</span> 部剧集
                    {activeCount > 0 && <span className="ml-1 text-foreground-500">（已筛选）</span>}
                  </p>
                </div>

                <div className="relative">
                  <select
                    aria-label="排序方式"
                    value={sortKey}
                    onChange={(event) => setSortKey(event.target.value as SortKey)}
                    className="h-9 cursor-pointer appearance-none rounded-md border border-background-300/60 bg-background-50 pl-3 pr-9 font-label text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
                  >
                    {(Object.keys(sortLabels) as SortKey[]).map((key) => (
                      <option key={key} value={key}>
                        {sortLabels[key]}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
                </div>
              </div>

              {activeCount > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {selectedTypes.map((type) => (
                    <button
                      key={`type-${type}`}
                      type="button"
                      onClick={() => toggleType(type)}
                      className="flex cursor-pointer items-center gap-1 rounded-full bg-accent-500/15 px-2.5 py-1 font-label text-[11px] text-accent-600 transition-colors hover:bg-accent-500/25"
                    >
                      类型 · {type}
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  ))}
                  {selectedYears.map((year) => (
                    <button
                      key={`year-${year}`}
                      type="button"
                      onClick={() => toggleYear(year)}
                      className="flex cursor-pointer items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 font-label text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {year} 年
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  ))}
                  {selectedRegions.map((region) => (
                    <button
                      key={`region-${region}`}
                      type="button"
                      onClick={() => toggleRegion(region)}
                      className="flex cursor-pointer items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 font-label text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {region}
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={reset}
                    className="cursor-pointer font-label text-[11px] text-foreground-500 underline underline-offset-2 hover:text-primary-500"
                  >
                    清空全部
                  </button>
                </div>
              )}

              {results.length > 0 ? (
                <>
                  <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-7 md:grid-cols-3 md:gap-x-5">
                    {visibleResults.map((drama) => (
                      <DramaCard key={drama.id} drama={drama} />
                    ))}
                  </div>

                  <div className="mt-9 flex flex-col items-center gap-3">
                    <p className="font-label text-[11px] text-foreground-500">
                      已显示 {visibleResults.length} / {results.length} 部剧集
                    </p>
                    <div className="h-1 w-48 overflow-hidden rounded-full bg-background-200">
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, (visibleResults.length / results.length) * 100)}%` }}
                      ></div>
                    </div>
                    {visibleResults.length < results.length ? (
                      <button
                        type="button"
                        onClick={() => setVisibleCount((prev) => prev + 6)}
                        className="mt-1 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 bg-background-100 px-5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-500"
                      >
                        <i className="ri-add-line text-sm"></i>
                        加载更多剧集
                      </button>
                    ) : (
                      <p className="mt-1 font-label text-xs text-foreground-500">
                        已经到底了，去搜索页找找别的剧吧
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
                    <i className="ri-inbox-line text-2xl"></i>
                  </span>
                  <p className="mt-4 font-heading text-lg text-foreground-900">暂无符合条件的剧集</p>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
                    试着减少筛选条件，或换个类型看看。也可以前往搜索页输入剧名、演员名快速定位。
                  </p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={reset}
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
                    >
                      重置筛选条件
                    </button>
                    <Link
                      to="/search"
                      className="cursor-pointer whitespace-nowrap rounded-md border border-background-300/60 px-4 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-600"
                    >
                      去搜索页
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />

      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选面板"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 cursor-pointer bg-background-50/80 backdrop-blur-sm"
          ></button>
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-background-300/60 bg-background-50 p-4 pb-8">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-heading text-base text-foreground-950">筛选剧集</p>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-300/60 text-foreground-700"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>
            {filterPanel}
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-4 w-full cursor-pointer rounded-md bg-primary-500 py-3 font-label text-sm font-medium text-background-50"
            >
              查看 {results.length} 部剧集
            </button>
          </div>
        </div>
      )}
    </div>
  );
}